/**
 * Microsoft Outlook Calendar Service
 * Uses Microsoft Graph API to create calendar events for deliverable deadlines
 *
 * Requires: User must be signed in with Microsoft account via MSAL
 * Scopes needed: Calendars.ReadWrite
 */

const GRAPH_CALENDAR_ENDPOINT = 'https://graph.microsoft.com/v1.0/me/events';

let _msalInstance = null;
let _account = null;

/**
 * Initialize the service with MSAL instance
 */
export function initCalendarService(msalInstance, account) {
  _msalInstance = msalInstance;
  _account = account;
}

/**
 * Get access token for Microsoft Graph API (Calendar scope)
 */
async function getAccessToken() {
  if (!_msalInstance || !_account) {
    throw new Error('Geen gebruiker ingelogd');
  }

  const request = {
    scopes: ['Calendars.ReadWrite'],
    account: _account,
  };

  try {
    const response = await _msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    console.log('[Calendar] Silent token failed, trying popup...');
    const response = await _msalInstance.acquireTokenPopup(request);
    return response.accessToken;
  }
}

/**
 * Lees de afspraken van vandaag uit de Outlook-agenda (silent-only; geen popup).
 * Geeft [] terug als er geen token (geen consent) of fout is — caller valt dan terug op Airtable.
 */
export async function getTodayCalendarEvents() {
  if (!_msalInstance || !_account) return [];
  let token;
  try {
    const resp = await _msalInstance.acquireTokenSilent({ scopes: ['Calendars.Read'], account: _account });
    token = resp.accessToken;
  } catch (e) {
    return []; // geen stille toestemming → fallback
  }
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  const url = `https://graph.microsoft.com/v1.0/me/calendarView?startDateTime=${start.toISOString()}&endDateTime=${end.toISOString()}&$orderby=start/dateTime&$top=30&$select=subject,start,end,location,isAllDay`;
  try {
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}`, Prefer: 'outlook.timezone="Europe/Amsterdam"' } });
    if (!resp.ok) return [];
    const data = await resp.json();
    return (data.value || []).map(e => ({
      subject: e.subject || 'Afspraak',
      start: e.start?.dateTime || null,
      end: e.end?.dateTime || null,
      allDay: !!e.isAllDay,
      location: e.location?.displayName || '',
    }));
  } catch (e) {
    return [];
  }
}

/**
 * Create a single calendar event
 * @param {Object} options - { subject, body, date (YYYY-MM-DD), startHour, durationMinutes }
 */
export async function createCalendarEvent({ subject, body, date, startHour = 15, durationMinutes = 30 }) {
  const token = await getAccessToken();

  const startDateTime = `${date}T${String(startHour).padStart(2, '0')}:00:00`;
  const endMinutes = startHour * 60 + durationMinutes;
  const endHour = Math.floor(endMinutes / 60);
  const endMin = endMinutes % 60;
  const endDateTime = `${date}T${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}:00`;

  const event = {
    subject,
    body: body ? { contentType: 'text', content: body } : undefined,
    start: { dateTime: startDateTime, timeZone: 'Europe/Amsterdam' },
    end: { dateTime: endDateTime, timeZone: 'Europe/Amsterdam' },
    isReminderOn: true,
    reminderMinutesBeforeStart: 60,
  };

  const response = await fetch(GRAPH_CALENDAR_ENDPOINT, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(event),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[Calendar] Create event failed:', errorText);
    throw new Error(`Agenda-event aanmaken mislukt: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create calendar events for multiple deliverable deadlines
 * @param {Array} deadlines - Array of { name, deadline (ISO date), projectName, deadlineType }
 * @returns {Promise<{created: Array, errors: Array}>}
 */
export async function createDeadlineEvents(deadlines) {
  const created = [];
  const errors = [];

  for (const d of deadlines) {
    try {
      const event = await createCalendarEvent({
        subject: `Deadline: ${d.name}`,
        body: `Project: ${d.projectName}\nType: ${d.deadlineType || 'Intern'}\n\nDeliverable deadline uit Full Frame planner.`,
        date: d.deadline.split('T')[0],
        startHour: 15,
        durationMinutes: 30,
      });
      created.push(event);
    } catch (error) {
      errors.push({ deadline: d.name, error: error.message });
    }
  }

  return { created, errors };
}
